import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import BookingService, { PackageBooking } from '../services/bookingService';
import QRCode from 'qrcode.react';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bookings-tabpanel-${index}`}
      aria-labelledby={`bookings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `bookings-tab-${index}`,
    'aria-controls': `bookings-tabpanel-${index}`,
  };
}

const MyBookingsPage: React.FC = () => {
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<PackageBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Fetch user bookings
  useEffect(() => {
    const fetchUserBookings = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userBookings = await BookingService.getUserBookings(currentUser.uid);
        setBookings(userBookings);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load your bookings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserBookings();
  }, [currentUser]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filter bookings based on tab
  const upcomingBookings = bookings.filter(booking => 
    booking.status === 'confirmed' && 
    booking.bookingDate && 
    new Date(booking.bookingDate.toDate()) >= new Date()
  );

  const pastBookings = bookings.filter(booking =>
    (booking.status === 'completed' || booking.status === 'confirmed') &&
    booking.bookingDate &&
    new Date(booking.bookingDate.toDate()) < new Date()
  );

  const cancelledBookings = bookings.filter(booking =>
    booking.status === 'cancelled' || booking.status === 'refunded'
  );

  // Handle booking cancellation
  const handleCancelBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to cancel this booking? This action cannot be undone.')) {
      return;
    }

    try {
      await BookingService.cancelBooking(bookingId);
      // Refresh bookings
      if (currentUser) {
        const updatedBookings = await BookingService.getUserBookings(currentUser.uid);
        setBookings(updatedBookings);
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  // Render booking card
  const renderBookingCard = (booking: PackageBooking) => {
    const bookingDate = booking.bookingDate?.toDate();
    const isPast = bookingDate ? new Date(bookingDate) < new Date() : false;
    const isUpcoming = !isPast && booking.status === 'confirmed';

    return (
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" component="h2">
              {booking.packageName}
            </Typography>
            <Chip
              label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              color={
                booking.status === 'confirmed' ? 'primary' :
                booking.status === 'completed' ? 'success' :
                booking.status === 'cancelled' ? 'error' : 'default'
              }
              size="small"
            />
          </Box>

          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            at {booking.venueName}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {bookingDate ? bookingDate.toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimeIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {booking.timeSlot.start} - {booking.timeSlot.end}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {booking.guests} {booking.guests === 1 ? 'Guest' : 'Guests'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" noWrap>
                  {booking.venueName}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Amount Paid
              </Typography>
              <Typography variant="h6" color="primary">
                ${booking.amount.toFixed(2)}
              </Typography>
            </Grid>
            {booking.status === 'confirmed' && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Redemption Code
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }}}>
                    <Typography variant="body1" fontFamily="monospace" fontWeight="bold">
                      {booking.redemptionCode}
                    </Typography>
                    <QrCodeIcon color="primary" sx={{ ml: 1 }} />
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/explore/packages/${booking.venueId}/${booking.packageId}`)}
            >
              View Package
            </Button>
            {isUpcoming && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleCancelBooking(booking.id)}
              >
                Cancel Booking
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => navigate(`/bookings/${booking.id}`)}
            >
              View Details
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Handle empty state
  const renderEmptyState = (type: string) => (
    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
      <QrCodeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        No {type} Bookings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {type === 'upcoming'
          ? "You don't have any upcoming bookings."
          : type === 'past'
          ? "You don't have any past bookings."
          : "You don't have any cancelled bookings."}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate('/explore/packages')}
      >
        Explore Packages
      </Button>
    </Paper>
  );

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Container>
    );
  }

  // Not logged in state
  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Please Sign In
          </Typography>
          <Typography variant="body1" paragraph>
            You need to be signed in to view your bookings.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
          >
            Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        My Bookings
      </Typography>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="booking tabs"
          variant="fullWidth"
        >
          <Tab label={`Upcoming (${upcomingBookings.length})`} {...a11yProps(0)} />
          <Tab label={`Past (${pastBookings.length})`} {...a11yProps(1)} />
          <Tab label={`Cancelled (${cancelledBookings.length})`} {...a11yProps(2)} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {upcomingBookings.length > 0 ? (
            upcomingBookings.map((booking) => renderBookingCard(booking))
          ) : (
            renderEmptyState('upcoming')
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {pastBookings.length > 0 ? (
            pastBookings.map((booking) => renderBookingCard(booking))
          ) : (
            renderEmptyState('past')
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {cancelledBookings.length > 0 ? (
            cancelledBookings.map((booking) => renderBookingCard(booking))
          ) : (
            renderEmptyState('cancelled')
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default MyBookingsPage;
